/**
 * Bone posing pipeline — FK rotation and IK translation.
 *
 * Extracted from storeCommandOps.ts for readability and isolation.
 * Pure logic: takes inputs (camera, mouse, bone state), returns new poses.
 */

import * as THREE from "three/webgpu";
import { visibleHeight } from "../app/storeCommandOps/transformMath.ts";
import type { AppState } from "../app/storeTypes.ts";
import { applySnapping } from "../modeling/snap.ts";
import { solveAnalytical2Bone } from "./analyticalIK.ts";
import { getBone as getBoneRef, getRestPose } from "./boneRegistry.ts";
import { applyConstraint, getConstraint } from "./constraints.ts";
import type { IKChainState } from "./ikChainState.ts";
import {
  isIKLogEnabled,
  logConstraintCorrection,
  logConstraintViolation,
  logPointerVsJoint,
  logWholeBodyApply,
} from "./ikDebugLog.ts";
import {
  getIKSession,
  measureConstraintDeviation,
  smoothQuaternions,
  updateSession,
} from "./ikStabilizer.ts";
import { solveJacobianIK } from "./jacobianIK.ts";
import { applySecondaryMotion } from "./secondaryMotion.ts";
import { solveWholeBodyIK, type WholeBodyTarget } from "./wholeBodyIK.ts";

// ---------------------------------------------------------------------------
// Tuning constants
// ---------------------------------------------------------------------------

/** View-bias strength for analytical (2-bone) IK: how much to keep
 *  the mid-joint (elbow/knee) near its original screen depth. */
const ANALYTICAL_VIEW_BIAS_STRENGTH = 0.6;

/** View-bias strength for Jacobian DLS IK. Applied as target depth
 *  projection rather than per-joint bias, since DLS works in angle space. */
const JACOBIAN_VIEW_BIAS_STRENGTH = 0.5;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Q4 = [number, number, number, number];

export interface BoneRotateParams {
  objectId: string;
  activeBone: string;
  selectedBones: ReadonlySet<string>;
  origLocalQuat: Q4;
  parentWorldQuat: Q4;
  axis: string | null;
  normalVector: [number, number, number] | null;
  /** When set, the rotation originated from a bone-local axis ring (pose mode).
   *  The rotation is post-multiplied in bone-local space to preserve euler
   *  axis independence, avoiding cross-axis bleed. */
  boneLocalAxis: "x" | "y" | "z" | null;
  camera: THREE.Camera;
  rect: DOMRect;
  screenX: number;
  screenY: number;
  ox: number;
  oy: number;
  snapshotBonePoses: Map<string, Map<string, Q4>>;
  lockedBones: ReadonlySet<string> | undefined;
  constraintWeight: number;
}

export interface BoneRotateResult {
  bonePoses: Map<string, Map<string, Q4>>;
}

export interface BoneTranslateParams {
  state: AppState;
  objectId: string;
  activeBone: string;
  chainState: IKChainState;
  origPositions: THREE.Vector3[];
  ikPoleDirection: [number, number, number] | undefined;
  ikSolverType: "analytical" | "jacobian";
  ikJointWeights: number[] | undefined;
  mannequinRotation: [number, number, number];
  axis: string | null;
  camera: THREE.Camera;
  rect: DOMRect;
  dx: number;
  dy: number;
  screenX: number;
  screenY: number;
  snapshotBonePoses: Map<string, Map<string, Q4>>;
  lockedBones: ReadonlySet<string> | undefined;
  pinnedBones: ReadonlyMap<string, [number, number, number]> | undefined;
  boneMap: Map<string, THREE.Bone> | undefined;
  constraintWeight: number;
}

export interface BoneTranslateResult {
  bonePoses: Map<string, Map<string, Q4>>;
  ikDragState: { isViolating: boolean; violationWorldPos: Q4Pos } | null;
}

type Q4Pos = [number, number, number];

// ---------------------------------------------------------------------------
// FK: Bone rotation — scratch objects (avoid per-frame allocations)
// ---------------------------------------------------------------------------

const _fkWorldPos = new THREE.Vector3();
const _fkPivotSS = new THREE.Vector3();
const _fkRotAxis = new THREE.Vector3();
const _fkCamFwd = new THREE.Vector3();
const _fkDeltaWorld = new THREE.Quaternion();
const _fkParentWorld = new THREE.Quaternion();
const _fkOrigLocal = new THREE.Quaternion();
const _fkParentInv = new THREE.Quaternion();
const _fkNewLocal = new THREE.Quaternion();
const _fkBoneWorld = new THREE.Quaternion();
const _fkRestQ = new THREE.Quaternion();
const _fkRestInv = new THREE.Quaternion();
const _fkDeltaFromRest = new THREE.Quaternion();
const _fkEuler = new THREE.Euler();

// ---------------------------------------------------------------------------
// FK: Bone rotation
// ---------------------------------------------------------------------------

/**
 * Apply FK rotation to selected bones. Computes a world-space delta rotation
 * from the screen-space angular drag, then applies it to each selected bone
 * with parent propagation and constraints.
 */
export function solveBoneRotate(
  params: BoneRotateParams,
): BoneRotateResult | null {
  const {
    objectId,
    activeBone,
    selectedBones,
    origLocalQuat,
    parentWorldQuat: pwq,
    axis,
    normalVector,
    boneLocalAxis,
    camera,
    rect,
    screenX,
    screenY,
    ox,
    oy,
    snapshotBonePoses,
    lockedBones,
    constraintWeight,
  } = params;

  const bone = getBoneRef(objectId, activeBone);
  if (!bone) return null;

  // Get active bone world position for screen-space pivot
  bone.getWorldPosition(_fkWorldPos);
  _fkPivotSS.copy(_fkWorldPos).project(camera);
  const pivotPxX = (_fkPivotSS.x * 0.5 + 0.5) * rect.width;
  const pivotPxY = (-_fkPivotSS.y * 0.5 + 0.5) * rect.height;

  // World-space rotation axis
  if (axis === "normal" && normalVector) {
    _fkRotAxis.set(normalVector[0], normalVector[1], normalVector[2]);
  } else if (axis === "x") _fkRotAxis.set(1, 0, 0);
  else if (axis === "y") _fkRotAxis.set(0, 1, 0);
  else if (axis === "z") _fkRotAxis.set(0, 0, 1);
  else {
    _fkRotAxis.set(0, 0, -1).applyQuaternion(camera.quaternion);
  }

  // Screen-space angular rotation around the pivot point
  const startAngle = Math.atan2(oy - pivotPxY, ox - pivotPxX);
  const currAngle = Math.atan2(screenY - pivotPxY, screenX - pivotPxX);
  let deltaAngle = startAngle - currAngle;
  if (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
  if (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

  // Flip when rotation axis points away from camera
  camera.getWorldDirection(_fkCamFwd);
  if (_fkRotAxis.dot(_fkCamFwd) > 0) deltaAngle = -deltaAngle;

  _fkDeltaWorld.setFromAxisAngle(_fkRotAxis, deltaAngle);

  const posesPatch = new Map(snapshotBonePoses);
  const bones = new Map(posesPatch.get(objectId) ?? new Map());

  // Sort selected bones by depth (shallowest first)
  const sortedBones = [...selectedBones].sort((a, b) => {
    let depthA = 0;
    let depthB = 0;
    let boneA = getBoneRef(objectId, a);
    let boneB = getBoneRef(objectId, b);
    while (boneA?.parent) {
      depthA++;
      boneA = boneA.parent as THREE.Bone;
    }
    while (boneB?.parent) {
      depthB++;
      boneB = boneB.parent as THREE.Bone;
    }
    return depthA - depthB;
  });

  const updatedWorldQuats = new Map<string, THREE.Quaternion>();

  for (const selectedBoneName of sortedBones) {
    if (lockedBones?.has(selectedBoneName)) continue;

    const selectedBoneRef = getBoneRef(objectId, selectedBoneName);
    if (!selectedBoneRef) continue;

    if (selectedBoneName === activeBone) {
      _fkParentWorld.set(pwq[0], pwq[1], pwq[2], pwq[3]);
      _fkOrigLocal.set(
        origLocalQuat[0],
        origLocalQuat[1],
        origLocalQuat[2],
        origLocalQuat[3],
      );
    } else {
      const parentName = (selectedBoneRef.parent as THREE.Bone)?.name;
      const updatedParent = parentName
        ? updatedWorldQuats.get(parentName)
        : undefined;
      if (updatedParent) {
        _fkParentWorld.copy(updatedParent);
      } else {
        _fkParentWorld.identity();
        if (selectedBoneRef.parent)
          selectedBoneRef.parent.getWorldQuaternion(_fkParentWorld);
      }
      const snapBonePoses = snapshotBonePoses.get(objectId);
      const snapQ = snapBonePoses?.get(selectedBoneName);
      if (snapQ) {
        _fkOrigLocal.set(snapQ[0], snapQ[1], snapQ[2], snapQ[3]);
      } else {
        _fkOrigLocal.copy(selectedBoneRef.quaternion);
      }
    }

    if (boneLocalAxis && selectedBoneName === activeBone) {
      // Euler-space addition: decompose existing rotation into euler angles,
      // add the drag angle to the single active component, rebuild.
      // This guarantees zero cross-axis bleed regardless of euler order.
      const restArr = getRestPose(objectId, selectedBoneName);
      if (restArr) {
        _fkRestQ.set(restArr[0], restArr[1], restArr[2], restArr[3]);
      } else {
        _fkRestQ.identity();
      }
      _fkRestInv.copy(_fkRestQ).invert();
      _fkDeltaFromRest.copy(_fkRestInv).multiply(_fkOrigLocal).normalize();
      _fkEuler.setFromQuaternion(_fkDeltaFromRest, "ZYX");
      _fkEuler[boneLocalAxis] += deltaAngle;

      // Pre-clamp to constraint range to prevent wrap-around. Without this,
      // dragging past the limit wraps the euler angle through ±π and the
      // constraint sees it as arriving from the opposite direction.
      const c = getConstraint(selectedBoneName);
      if (c?.type === "per_axis") {
        const minKey = `${boneLocalAxis}Min` as "xMin" | "yMin" | "zMin";
        const maxKey = `${boneLocalAxis}Max` as "xMax" | "yMax" | "zMax";
        _fkEuler[boneLocalAxis] = Math.max(
          c[minKey],
          Math.min(c[maxKey], _fkEuler[boneLocalAxis]),
        );
      }

      _fkNewLocal
        .copy(_fkRestQ)
        .multiply(new THREE.Quaternion().setFromEuler(_fkEuler));
      _fkNewLocal.normalize();
    } else {
      // World-space conjugation (for free rotation, non-active bones, etc.)
      // new_local = inv(parent_world) * delta_world * parent_world * orig_local
      _fkParentInv.copy(_fkParentWorld).invert();
      _fkNewLocal
        .copy(_fkParentInv)
        .multiply(_fkDeltaWorld)
        .multiply(_fkParentWorld)
        .multiply(_fkOrigLocal);
      _fkNewLocal.normalize();
    }

    if (constraintWeight > 0) {
      const restQ = getRestPose(objectId, selectedBoneName);
      const axisHint =
        boneLocalAxis && selectedBoneName === activeBone ? boneLocalAxis : null;
      _fkNewLocal.copy(
        applyConstraint(
          selectedBoneName,
          _fkNewLocal,
          restQ,
          constraintWeight,
          axisHint,
        ),
      );
    }

    bones.set(selectedBoneName, [
      _fkNewLocal.x,
      _fkNewLocal.y,
      _fkNewLocal.z,
      _fkNewLocal.w,
    ]);
    _fkBoneWorld.copy(_fkParentWorld).multiply(_fkNewLocal);
    updatedWorldQuats.set(selectedBoneName, _fkBoneWorld.clone());
  }

  // Secondary motion (e.g., clavicle follows arm elevation)
  const fkDirectBones = new Set(selectedBones);
  const fkSecondary = applySecondaryMotion(
    objectId,
    fkDirectBones,
    bones,
    constraintWeight,
  );
  for (const [name, q] of fkSecondary) bones.set(name, q);

  posesPatch.set(objectId, bones);
  return { bonePoses: posesPatch };
}

// ---------------------------------------------------------------------------
// IK: Bone translation
// ---------------------------------------------------------------------------

/**
 * Project mouse delta to a world-space movement vector at a given pivot depth.
 */
function projectMouseToWorld(
  pivotPos: THREE.Vector3,
  axis: string | null,
  dx: number,
  dy: number,
  camera: THREE.Camera,
  rect: DOMRect,
): THREE.Vector3 {
  const camDist = camera.position.distanceTo(pivotPos);
  const vh = visibleHeight(camera, camDist);
  const worldPerPx = vh / rect.height;

  const camRight = new THREE.Vector3();
  const camUp = new THREE.Vector3();
  const camFwd = new THREE.Vector3();
  camera.matrixWorld.extractBasis(camRight, camUp, camFwd);

  if (axis === "x" || axis === "y" || axis === "z") {
    const axisVec = new THREE.Vector3(
      axis === "x" ? 1 : 0,
      axis === "y" ? 1 : 0,
      axis === "z" ? 1 : 0,
    );
    const axisSS = pivotPos.clone().add(axisVec).project(camera);
    const originSS = pivotPos.clone().project(camera);
    const axisDirSS = new THREE.Vector2(
      axisSS.x - originSS.x,
      axisSS.y - originSS.y,
    );
    if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
    const ndcDx = (dx / rect.width) * 2;
    const ndcDy = -(dy / rect.height) * 2;
    const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
    const worldDist = proj * vh;
    return axisVec.multiplyScalar(worldDist);
  }

  return camRight
    .clone()
    .multiplyScalar(dx * worldPerPx)
    .add(camUp.clone().multiplyScalar(-dy * worldPerPx));
}

/**
 * Full IK translation pipeline: solver dispatch, stabilization, constraints,
 * violation detection, secondary motion.
 */
export function solveBoneTranslate(
  params: BoneTranslateParams,
): BoneTranslateResult | null {
  const {
    objectId,
    activeBone,
    chainState,
    origPositions,
    ikPoleDirection,
    ikSolverType,
    ikJointWeights,
    mannequinRotation,
    axis,
    camera,
    rect,
    dx,
    dy,
    snapshotBonePoses,
    lockedBones,
    pinnedBones,
    boneMap,
    constraintWeight,
  } = params;

  // Whole-body IK when pins exist — solves the full skeleton tree
  if (pinnedBones && pinnedBones.size > 0 && boneMap) {
    return solveWholeBodyTranslate(params);
  }

  // Compute target world position
  const effectorPos = origPositions[origPositions.length - 1];
  const moveVec = projectMouseToWorld(effectorPos, axis, dx, dy, camera, rect);

  applySnapping(
    params.state,
    params.screenX,
    params.screenY,
    params.rect,
    effectorPos,
    moveVec,
    axis as any,
    null,
    new Set([objectId]),
  );

  const target = effectorPos.clone().add(moveVec);

  // Compute pole direction in world space
  let poleVec: THREE.Vector3 | undefined;
  if (ikPoleDirection) {
    poleVec = new THREE.Vector3(
      ikPoleDirection[0],
      ikPoleDirection[1],
      ikPoleDirection[2],
    );
    const [rx, ry, rz] = mannequinRotation;
    const objQuat = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(rx, ry, rz),
    );
    poleVec.applyQuaternion(objQuat);
  }

  // Debug logging
  if (isIKLogEnabled()) {
    const jointDelta = target.clone().sub(effectorPos);
    const camFwd = new THREE.Vector3();
    camera.getWorldDirection(camFwd);
    const camRightDir = new THREE.Vector3();
    const camUpDir = new THREE.Vector3();
    camera.matrixWorld.extractBasis(camRightDir, camUpDir, camFwd);
    logPointerVsJoint(
      dx,
      dy,
      jointDelta.dot(camRightDir),
      jointDelta.dot(camUpDir),
      jointDelta.dot(camFwd),
      ikSolverType,
    );
  }

  let solvedQuats: Map<string, Q4>;

  if (
    ikSolverType === "analytical" &&
    chainState.boneNames.length >= 2 &&
    poleVec
  ) {
    // ---- Analytical 2-bone IK for limbs ----
    const rootPos = origPositions[0];
    const solveBoneNames = chainState.boneNames.slice(0, 2);
    const solveOrigPositions = origPositions.slice(0, 3);
    const solveLocalQuats = chainState.startLocalQuats.slice(0, 2);
    const solveParentQuats = chainState.parentWorldQuats.slice(0, 2);

    const camForward = new THREE.Vector3();
    camera.getWorldDirection(camForward);
    const origMidDepth = origPositions[1].dot(camForward);

    solvedQuats = solveAnalytical2Bone(
      rootPos,
      target,
      chainState.lengths[0],
      chainState.lengths[1],
      poleVec,
      solveOrigPositions,
      solveBoneNames,
      solveLocalQuats,
      solveParentQuats,
      {
        forward: camForward,
        origMidDepth,
        strength: ANALYTICAL_VIEW_BIAS_STRENGTH,
      },
    );
    solvedQuats.delete(activeBone);

    const isFirstFrame = !getIKSession()?.initialized;
    if (!isFirstFrame) {
      smoothQuaternions(solvedQuats, target);
    }
  } else {
    // ---- Jacobian DLS for all non-analytical chains ----
    const session = getIKSession();
    const isFirstFrame = !session?.initialized;

    // View-plane depth bias: pull target toward original effector depth
    // to keep motion primarily in the screen plane.
    const camForward = new THREE.Vector3();
    camera.getWorldDirection(camForward);
    const origEffectorDepth =
      origPositions[origPositions.length - 1].dot(camForward);
    const currentTargetDepth = target.dot(camForward);
    target.addScaledVector(
      camForward,
      -(currentTargetDepth - origEffectorDepth) * JACOBIAN_VIEW_BIAS_STRENGTH,
    );

    // Warm-start from previous frame's quaternions for smooth continuity
    const warmStartQuats = !isFirstFrame ? session?.prevQuats : undefined;

    solvedQuats = solveJacobianIK(chainState, target, {
      jointWeights: ikJointWeights,
      objectId,
      constraintWeight,
      warmStartQuats: warmStartQuats || undefined,
      excludeLastBone: true,
    });
    solvedQuats.delete(activeBone);

    if (!isFirstFrame) {
      smoothQuaternions(solvedQuats, target);
    }
  }

  // Build pre-constraint snapshot
  const preConstraintQuats = new Map<string, Q4>();
  for (const [name, q] of solvedQuats) {
    preConstraintQuats.set(name, [...q] as Q4);
  }

  // Apply constraints
  const posesPatch = new Map(snapshotBonePoses);
  const bonePoses = new Map(posesPatch.get(objectId) ?? new Map());
  const postConstraintQuats = new Map<string, Q4>();

  for (const [boneName, q] of solvedQuats) {
    if (lockedBones?.has(boneName)) continue;

    if (constraintWeight <= 0) {
      // Skip constraints — use raw solver output
      const tuple: Q4 = [...q] as Q4;
      postConstraintQuats.set(boneName, tuple);
      bonePoses.set(boneName, tuple);
    } else {
      const preQuat = new THREE.Quaternion(q[0], q[1], q[2], q[3]);
      const boneRestQ = getRestPose(objectId, boneName);
      const postQuat = applyConstraint(
        boneName,
        preQuat,
        boneRestQ,
        constraintWeight,
      );

      if (isIKLogEnabled()) {
        const preAngle = 2 * Math.acos(Math.min(1, Math.abs(preQuat.w)));
        const postAngle = 2 * Math.acos(Math.min(1, Math.abs(postQuat.w)));
        const cDef = getConstraint(boneName);
        logConstraintCorrection(
          boneName,
          cDef?.type ?? "none",
          preAngle,
          postAngle,
        );
      }

      const tuple: Q4 = [postQuat.x, postQuat.y, postQuat.z, postQuat.w];
      postConstraintQuats.set(boneName, tuple);
      bonePoses.set(boneName, tuple);
    }
  }

  // Violation detection (skip when constraints at zero)
  let ikDragState: BoneTranslateResult["ikDragState"] = null;
  if (constraintWeight > 0) {
    const deviation = measureConstraintDeviation(
      preConstraintQuats,
      postConstraintQuats,
    );
    const violationWorldPos = target.clone();

    const session = getIKSession();
    const frozen = session
      ? updateSession(
          target,
          violationWorldPos,
          deviation,
          postConstraintQuats,
          preConstraintQuats,
        )
      : false;

    if (isIKLogEnabled()) {
      logConstraintViolation(deviation, frozen && !!session);
    }

    if (frozen && session) {
      for (const [name, q] of session.lastValidQuats) {
        bonePoses.set(name, q);
      }
    }

    ikDragState = session
      ? {
          isViolating: session.isViolating,
          violationWorldPos: [
            violationWorldPos.x,
            violationWorldPos.y,
            violationWorldPos.z,
          ],
        }
      : null;
  } else {
    // No constraints → still update stabilizer session for warm-starting
    const session = getIKSession();
    if (session) {
      updateSession(
        target,
        target.clone(),
        0,
        postConstraintQuats,
        preConstraintQuats,
      );
    }
  }

  // Secondary motion
  const ikDirectBones = new Set(solvedQuats.keys());
  const ikSecondary = applySecondaryMotion(
    objectId,
    ikDirectBones,
    bonePoses,
    constraintWeight,
  );
  for (const [name, q] of ikSecondary) bonePoses.set(name, q);

  posesPatch.set(objectId, bonePoses);
  return { bonePoses: posesPatch, ikDragState };
}

// ---------------------------------------------------------------------------
// Whole-body IK: pin-constrained full-skeleton solve
// ---------------------------------------------------------------------------

/**
 * Solve IK for the entire skeleton when pinned bones exist.
 * Pins are highest priority — they stay put. The dragged bone reaches
 * toward the mouse target. All other joints adapt via null-space projection.
 */
function solveWholeBodyTranslate(
  params: BoneTranslateParams,
): BoneTranslateResult | null {
  const {
    objectId,
    activeBone,
    origPositions,
    axis,
    camera,
    rect,
    dx,
    dy,
    snapshotBonePoses,
    lockedBones,
    pinnedBones,
    boneMap,
    constraintWeight,
  } = params;

  if (!pinnedBones || !boneMap) return null;

  // Mouse target in world space
  const effectorPos = origPositions[origPositions.length - 1];
  const moveVec = projectMouseToWorld(effectorPos, axis, dx, dy, camera, rect);

  applySnapping(
    params.state,
    params.screenX,
    params.screenY,
    params.rect,
    effectorPos,
    moveVec,
    axis as any,
    null,
    new Set([objectId]),
  );

  const target = effectorPos.clone().add(moveVec);

  // Build IK targets
  const targets: WholeBodyTarget[] = [];

  // Pin targets (skip if same bone is being dragged — drag target takes priority)
  for (const [boneName, pos] of pinnedBones) {
    if (boneName === activeBone) continue;
    targets.push({
      boneName,
      position: new THREE.Vector3(pos[0], pos[1], pos[2]),
      weight: 1.0,
    });
  }

  // Drag target
  targets.push({
    boneName: activeBone,
    position: target,
    weight: 1.0,
  });

  // Current poses for warm-starting
  const session = getIKSession();
  const isFirstFrame = !session?.initialized;
  const warmStartQuats = !isFirstFrame ? session?.prevQuats : undefined;
  const currentPoses = snapshotBonePoses.get(objectId);

  // Solve
  const solvedQuats = solveWholeBodyIK(
    boneMap,
    targets,
    objectId,
    currentPoses,
    lockedBones,
    constraintWeight,
    warmStartQuats || undefined,
  );

  // Keep the dragged bone's solved quaternion — unlike chain-based IK where the
  // effector tip doesn't need rotation, the whole-body solver computes the dragged
  // bone's rotation to maintain consistency with pinned children. Deleting it would
  // break FK consistency (e.g., dragging shinL while footL is pinned: the solver
  // sets shinL's rotation so footL stays put, removing it uses the stale snapshot).
  // Only remove root (weight=0, never participates).
  solvedQuats.delete("root");

  // No smoothQuaternions or violation freezing here — the whole-body solver
  // handles its own convergence via iterative DLS with warm-starting.
  // The angular-velocity clamper would fight intentional leg corrections.

  if (isIKLogEnabled()) {
    logWholeBodyApply(activeBone, solvedQuats, isFirstFrame);
  }

  // Apply to bone poses
  const posesPatch = new Map(snapshotBonePoses);
  const bonePoses = new Map(posesPatch.get(objectId) ?? new Map());

  for (const [boneName, q] of solvedQuats) {
    if (lockedBones?.has(boneName)) continue;
    bonePoses.set(boneName, q);
  }

  // Update stabilizer session (for warm-start only, no freezing)
  let ikDragState: BoneTranslateResult["ikDragState"] = null;
  if (session) {
    const quats = new Map(solvedQuats);
    updateSession(target, target, 0, quats, quats);

    ikDragState = {
      isViolating: false,
      violationWorldPos: [target.x, target.y, target.z],
    };
  }

  // Secondary motion
  const directBones = new Set(solvedQuats.keys());
  const secondary = applySecondaryMotion(
    objectId,
    directBones,
    bonePoses,
    constraintWeight,
  );
  for (const [name, q] of secondary) bonePoses.set(name, q);

  posesPatch.set(objectId, bonePoses);
  return { bonePoses: posesPatch, ikDragState };
}
